import type { TeamNotifRecipients } from '@/proto/logiblocks/logiblocks';
import type { TeamData, TeamUserSelection } from '@/components/TeamUserSelect';
import {
  customerLegEnabled,
  withLogiblocksContactRoles,
} from '../shared/contactRoles';

export interface AdditionalOption {
  id: string;
  name: string;
  disabled?: boolean;
}

/**
 * Convert TeamUserSelection (from TeamUserSelect component) to
 * TeamNotifRecipients[] (protobuf schema), grouping individual
 * userIds by their team. Unmapped/additional userIds are assigned to an object
 * with additional_recipients_ids, teamId="", selectAll=false, userIds=[].
 */
export function teamUserSelectionToTeamNotifRecipients(
  selection: TeamUserSelection,
  teamsData: TeamData[]
): TeamNotifRecipients[] {
  const result: TeamNotifRecipients[] = [];

  for (const teamId of selection.teamIds) {
    result.push({
      team_id: teamId,
      select_all: true,
      user_ids: [],
      additional_recipients: [],
    });
  }

  const userIdToTeamId = new Map<string, string>();
  for (const team of teamsData) {
    for (const user of team.users) {
      if (!userIdToTeamId.has(user.id)) {
        userIdToTeamId.set(user.id, team.teamId);
      }
    }
  }

  const byTeam = new Map<string, string[]>();
  const additionalRecipientIds: string[] = [];

  for (const userId of selection.userIds) {
    const teamId = userIdToTeamId.get(userId);
    if (teamId) {
      const existing = byTeam.get(teamId);
      if (existing) {
        existing.push(userId);
      } else {
        byTeam.set(teamId, [userId]);
      }
    } else {
      additionalRecipientIds.push(userId);
    }
  }

  byTeam.forEach((userIds, teamId) => {
    result.push({
      team_id: teamId,
      select_all: false,
      user_ids: userIds,
      additional_recipients: [],
    });
  });

  if (additionalRecipientIds.length > 0) {
    result.push({
      team_id: '',
      select_all: false,
      user_ids: [],
      additional_recipients: additionalRecipientIds,
      is_additional: true,
    });
  }

  return result;
}

/**
 * Convert TeamNotifRecipients[] (protobuf) back to TeamUserSelection
 * (for TeamUserSelect component).
 */
export function teamNotifRecipientsToTeamUserSelection(
  recipients: Array<
    TeamNotifRecipients & {
      additional_recipients_ids?: string[];
      additionalRecipientsIds?: string[];
    }
  >
): TeamUserSelection {
  const teamIds: string[] = [];
  const userIds: string[] = [];

  for (const r of recipients ?? []) {
    const isAdd = r.is_additional;
    if (r.select_all && !isAdd) {
      if (r.team_id) teamIds.push(r.team_id);
    } else {
      if (r.user_ids && r.user_ids.length > 0) {
        userIds.push(...r.user_ids);
      }
      const addIds =
        r.additional_recipients ||
        r.additional_recipients_ids ||
        r.additionalRecipientsIds;
      if (addIds && addIds.length > 0) {
        userIds.push(...addIds);
      }
    }
  }

  return { teamIds, userIds };
}

/**
 * Transform the API response (teams_users) into the TeamData[] shape
 * expected by TeamUserSelect.
 */
export function apiTeamsResponseToTeamData(
  apiTeams: Array<{
    id: string;
    name: string;
    color?: string;
    users: Array<{ id: string; name?: string; email?: string }>;
  }>
): TeamData[] {
  return apiTeams.map((team) => ({
    teamId: team.id,
    teamName: team.name,
    color: team.color,
    users: team.users.map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      teamId: team.id,
    })),
  }));
}

/**
 * Transform the API response (additional options) into AdditionalOption[]
 * shape expected by TeamUserSelect.
 */
export function apiTeamsResponseToAdditionalOptions(
  additional?: Array<{ key: string; label: string; enabled?: boolean }>
): AdditionalOption[] {
  const allowedKeys = new Set([
    'ORIGIN_PRIMARY_CONTACT',
    'DESTINATION_PRIMARY_CONTACT',
    'CONTROLLING_OFFICE_PRIMARY_CONTACT',
  ]);

  const fallback: AdditionalOption[] = [
    { id: 'ORIGIN_PRIMARY_CONTACT', name: 'Origin primary contact' },
    {
      id: 'DESTINATION_PRIMARY_CONTACT',
      name: 'Destination primary contact',
    },
    { id: 'CONTROLLING_OFFICE_PRIMARY_CONTACT', name: 'Controlling office' },
  ];

  if (!additional || additional.length === 0) {
    return fallback;
  }

  const filtered = additional
    .filter((item) => allowedKeys.has(item.key))
    .map((item) => ({
      id: item.key,
      name: item.label,
      disabled: item.enabled === false,
    }));

  if (filtered.length === 0) {
    return fallback;
  }

  return filtered;
}

/** Adds LogiBlocks' secondary-contact and pickup/drop office roles (see contactRoles)
 * to the Email step's role options. */
export function withLogiblocksEmailRoles(
  options: AdditionalOption[]
): AdditionalOption[] {
  return withLogiblocksContactRoles(
    options.map((option) => ({
      value: option.id,
      label: option.name,
      disabled: option.disabled,
    })),
    { customerLeg: customerLegEnabled() }
  ).map((option) => ({
    id: option.value,
    name: option.label,
    disabled: option.disabled,
  }));
}
